import { Icon } from "@chakra-ui/react";
import clsx from "clsx";
import { IconType } from "react-icons";
import { GrLock } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

import styles from "./styles.module.css";
import { LucideLockKeyhole, ChevronDown, ChevronUp } from "lucide-react";

export interface SubNavItem {
	name: string;
	url: string;
}

export interface SideNavItemProps {
	name: string;
	icon: IconType;
	path?: string;
	locked?: boolean;
	active?: boolean;
	onClose?: () => void;
	subItems?: SubNavItem[];
	isActive?: (url: string) => boolean;
	onSubItemClick?: (url: string) => void;
}

export const SideNavItem = ({
	icon,
	name,
	path,
	locked,
	active,
}: SideNavItemProps) => {
	const navigate = useNavigate();

	const handleClick = () => {
		if (!locked && path) {
			navigate(path);
		}
	};

	return (
		<div
			className="grid grid-cols-[36px_1fr] w-full items-center pr-8 cursor-pointer"
			onClick={handleClick}
		>
			<div className={clsx("h-full w-1", active && "bg-accent-500")}>
				{/* active bar */}
			</div>
			<div
				className={clsx(
					"grid grid-cols-[36px_1fr_24px] w-full items-center py-3 px-3 rounded-lg",
					active && "bg-accent-100"
				)}
			>
				<div>
					<Icon
						as={icon}
						w={5}
						h={5}
						className={styles.icon}
						color={active ? "brand.500" : "primary.200"}
					/>
				</div>
				<div
					className={clsx(
						"text-sm text-primary-200 font-medium",
						active && "!text-accent-500 !font-semibold"
					)}
				>
					{name}
				</div>
				<div className="justify-self-end">
					{locked && (
						<Icon
							as={GrLock}
							w={4}
							h={4}
							className={styles.icon}
							color={active ? "brand.500" : "primary.200"}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export const SideNavItemV2 = ({
	icon,
	name,
	path,
	locked,
	active,
	onClose,
	subItems,
	isActive,
	onSubItemClick,
}: SideNavItemProps) => {
	const navigate = useNavigate();
	const hasSubItems = subItems && subItems.length > 0;
	const [isExpanded, setIsExpanded] = useState(false);

	const handleClick = () => {
		if (hasSubItems) {
			// Toggle expand/collapse for items with sub-items (only if not locked)
			if (!locked) {
				setIsExpanded((prev) => !prev);
			}
		} else if (path) {
			// Navigate for regular items
			onClose && onClose();
			if (!locked) {
				path.startsWith("http") ? window.open(path, "_blank") : navigate(path);
			}
		}
	};

	const handleSubItemClick = (url: string) => {
		if (onSubItemClick) {
			onSubItemClick(url);
		} else {
			// Fallback to default navigation
			url.startsWith("http") ? window.open(url, "_blank") : navigate(url);
		}
	};

	return (
		<div className="w-full">
			<div
				className={clsx(
					"flex w-full justify-between items-center lg:px-6",
					locked && hasSubItems ? "cursor-not-allowed" : "cursor-pointer"
				)}
				onClick={handleClick}
			>
				<div
					className={clsx(
						"w-full flex items-center justify-between px-2 lg:px-3 py-2 lg:py-3",
						active && "bg-[#EBEBEB]"
					)}
				>
					<div className="flex items-center gap-3 lg:gap-4">
						<div>
							<Icon
								as={icon}
								w={5}
								h={5}
								color={active ? "#2F1612" : "#828282"}
							/>
						</div>
						<div
							className={clsx(
								"text-base lg:text-lg text-[#828282] font-medium",
								active && "!text-[#2F1612]",
								locked && "max-w-[145px] truncate"
							)}
						>
							{name}
						</div>
					</div>
					<div>
						{hasSubItems && (
							<>
								{isExpanded ? (
									<ChevronUp className="w-5 h-5 text-[#828282]" />
								) : (
									<ChevronDown className="w-5 h-5 text-[#828282]" />
								)}
							</>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2">
					{locked && (
						<Icon
							as={LucideLockKeyhole}
							w={5}
							h={5}
							className={styles.icon}
							color={active ? "#2F1612" : "#828282"}
						/>
					)}
				</div>
			</div>
			{hasSubItems && isExpanded && subItems && subItems.length > 0 && (
				<ul className="flex flex-col gap-2 mt-2 border-l-[1px] border-l-[#191818] lg:mx-6 bg-[#EBEBEB]">
					{subItems.map((subItem) => {
						const subItemActive = isActive ? isActive(subItem.url) : false;

						return (
							<li key={subItem.name}>
								<div
									className={clsx(
										"flex items-center gap-3 lg:gap-4 px-2 lg:px-3 py-2 lg:py-3 cursor-pointer rounded-lg"
									)}
									onClick={() => handleSubItemClick(subItem.url)}
								>
									<div
										className={clsx(
											"text-sm lg:text-base text-[#828282] font-medium",
											subItemActive && "!text-[#2F1612]"
										)}
									>
										{subItem.name}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};
